import React from 'react';
import Wahanda from 'common/wahanda';
import type {
  EnrichedLegacySalesReportRow,
  EnrichedLegacySalesReportView,
} from 'services/sales-report';

const labels = () => Wahanda.lang.reports.salesReports.report.labels;

const rowHasTtv = (row: EnrichedLegacySalesReportRow | null | undefined): boolean =>
  row != null && row.ttv != null && row.ttv !== '';

const ttvDisplay = (row: EnrichedLegacySalesReportRow): string =>
  typeof row.ttv === 'object' ? '' : String(row.ttv ?? '');

const TOP_SERVICES_CONDITIONAL = /\{\{#fiveServices\}\}([\s\S]*?)\{\{\/fiveServices\}\}/g;

const formatTopServicesHeading = (fiveServices: boolean): string => {
  const raw = labels().topServices;
  if (raw == null) {
    return '';
  }
  const template = typeof raw === 'string' ? raw : String(raw);
  if (!template) {
    return '';
  }
  if (!template.includes('{{#fiveServices}}')) {
    return template;
  }
  return template.replace(
    TOP_SERVICES_CONDITIONAL,
    fiveServices ? (_match, inner: string) => inner : () => '',
  );
};

const GeneralHeaderRow = ({
  showAdditionalServices,
  showTtvLabel,
}: {
  showAdditionalServices: boolean;
  showTtvLabel: boolean;
}) => (
  <tr>
    <th>&nbsp;</th>
    {showAdditionalServices && (
      <>
        <th>{labels().primaryService}</th>
        <th>{labels().additionalServices}</th>
      </>
    )}
    <th>{showTtvLabel ? labels().ttv : null}</th>
    <th>{labels().count}</th>
  </tr>
);

const DataRow = ({
  row,
  showAdditionalServices,
}: {
  row: EnrichedLegacySalesReportRow;
  showAdditionalServices: boolean;
}) => (
  <tr>
    <td>
      <span>{row.label}</span>
    </td>
    {showAdditionalServices && (
      <>
        <td>
          <span>{row.basic}</span>
        </td>
        <td>
          <span>{row.additional}</span>
        </td>
      </>
    )}
    <td>
      <span>{ttvDisplay(row)}</span>
    </td>
    <td>
      <span>{row.bookings}</span>
    </td>
  </tr>
);

const LabeledSectionHeaderRow = ({
  title,
  showAdditionalServices,
}: {
  title: string;
  showAdditionalServices: boolean;
}) => (
  <tr>
    <th>
      <h3>{title}</h3>
    </th>
    {showAdditionalServices && (
      <>
        <th>{labels().primaryService}</th>
        <th>{labels().additionalServices}</th>
      </>
    )}
    <th>{labels().ttv}</th>
    <th>{labels().count}</th>
  </tr>
);

type Props = {
  data: EnrichedLegacySalesReportView;
};

export const SalesReportContent = ({ data }: Props) => {
  const { total } = data;
  const showTtvLabelInGeneral = total != null && rowHasTtv(total);

  return (
    <>
      {data.timeStamp ? (
        <div className="printed-date">
          <span className="title">{labels().date}</span>
          <span className="date">{data.timeStamp}</span>
        </div>
      ) : null}
      <h1>{labels().summary}</h1>
      <h2>{data.venueName}</h2>
      {total != null ? (
        <div className="stats-group general">
          <table>
            <thead>
              <GeneralHeaderRow
                showAdditionalServices={data.showAdditionalServices}
                showTtvLabel={showTtvLabelInGeneral}
              />
            </thead>
            <tbody>
              <tr className="important">
                <td>
                  <h3 className="period">{data.period}</h3>
                  <span className="date">{data.dates}</span>
                </td>
                {data.showAdditionalServices && (
                  <>
                    <td>
                      <span>{total.basic}</span>
                    </td>
                    <td>
                      <span>{total.additional}</span>
                    </td>
                  </>
                )}
                <td>
                  <span className="highlighted">{ttvDisplay(total)}</span>
                </td>
                <td>
                  <span>{total.bookings}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
      {data.showEmployees ? (
        <div className="stats-group">
          <table>
            <thead>
              <LabeledSectionHeaderRow
                title={labels().byEmployee}
                showAdditionalServices={data.showAdditionalServices}
              />
            </thead>
            <tbody>
              {data.employees.map((row, i) => (
                <DataRow
                  key={`e-${row.label ?? ''}-${i}`}
                  row={row}
                  showAdditionalServices={data.showAdditionalServices}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {data.showServiceGroups ? (
        <div className="stats-group">
          <table>
            <thead>
              <LabeledSectionHeaderRow
                title={labels().byServiceGroup}
                showAdditionalServices={data.showAdditionalServices}
              />
            </thead>
            <tbody>
              {data.serviceGroups.map((row, i) => (
                <DataRow
                  key={`g-${row.label ?? ''}-${i}`}
                  row={row}
                  showAdditionalServices={data.showAdditionalServices}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {data.showTopServices ? (
        <div className="stats-group">
          <table>
            <thead>
              <LabeledSectionHeaderRow
                title={formatTopServicesHeading(data.fiveServices)}
                showAdditionalServices={data.showAdditionalServices}
              />
            </thead>
            <tbody>
              {data.topServices.map((row, i) => (
                <DataRow
                  key={`t-${row.label ?? ''}-${i}`}
                  row={row}
                  showAdditionalServices={data.showAdditionalServices}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {data.showChannels ? (
        <div className="stats-group">
          <table>
            <thead>
              <LabeledSectionHeaderRow
                title={labels().byChannel}
                showAdditionalServices={data.showAdditionalServices}
              />
            </thead>
            <tbody>
              {data.channels.map((row, i) => (
                <DataRow
                  key={`c-${row.label ?? ''}-${i}`}
                  row={row}
                  showAdditionalServices={data.showAdditionalServices}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
};
